// Performance monitoring utilities
export const performanceUtils = {
  measureComponentLoad: (componentName: string) => {
    if (typeof window !== 'undefined' && 'performance' in window) {
      const start = performance.now();
      return () => {
        const end = performance.now();
        console.log(`${componentName} loaded in ${end - start}ms`);
      };
    }
    return () => {};
  },
  
  measureFunctionExecution: <T extends (...args: unknown[]) => unknown>(
    fn: T,
    name: string
  ): T => {
    return ((...args: Parameters<T>) => {
      const start = performance.now();
      const result = fn(...args);
      const end = performance.now();
      console.log(`${name} executed in ${end - start}ms`);
      return result;
    }) as T;
  },

  // Memory usage monitoring - محسن لمراقبة الذاكرة
  getMemoryUsage: () => {
    if (typeof window !== 'undefined' && 'memory' in performance) {
      const memory = (performance as Performance & { memory: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
      return {
        used: Math.round(memory.usedJSHeapSize / 1048576 * 100) / 100,
        total: Math.round(memory.totalJSHeapSize / 1048576 * 100) / 100,
        limit: Math.round(memory.jsHeapSizeLimit / 1048576 * 100) / 100
      };
    }
    return null;
  },

  // Debounce with performance optimization - محسن للـ debounce
  debounce: <T extends (...args: unknown[]) => unknown>(
    func: T,
    wait: number,
    immediate = false
  ): ((...args: Parameters<T>) => void) => {
    let timeout: NodeJS.Timeout | null = null;
    return (...args: Parameters<T>) => {
      const later = () => {
        timeout = null;
        if (!immediate) func(...args);
      };
      const callNow = immediate && !timeout;
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func(...args);
    };
  },

  // Throttle with performance optimization - محسن للـ throttle
  throttle: <T extends (...args: unknown[]) => unknown>(
    func: T,
    limit: number
  ): ((...args: Parameters<T>) => void) => {
    let inThrottle: boolean;
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  },

  // ============================================================================
  // TEMPLATE SELECTION PERFORMANCE OPTIMIZATIONS
  // ============================================================================
  
  // Preload template assets for faster switching - محسن لتحميل القوالب
  preloadTemplateAssets: (templateId: string) => {
    if (typeof window === 'undefined') return;
    // فقط تجهيز الصورة (بدون تحميل CSS/JS)
    const img = new Image();
    img.src = `/templates/${templateId}.svg`;
  },

  // Optimize template switching with requestAnimationFrame - محسن لتبديل القوالب
  optimizedTemplateSwitch: (callback: () => void) => {
    if (typeof window === 'undefined') {
      callback();
      return;
    }
    
    requestAnimationFrame(() => {
      requestAnimationFrame(callback);
    });
  },

  // Batch template updates for better performance - محسن لتحديثات القوالب
  batchTemplateUpdates: (() => {
    let batchQueue: (() => void)[] = [];
    let isProcessing = false;
    
    const processBatch = () => {
      if (isProcessing || batchQueue.length === 0) return;
      
      isProcessing = true;
      requestAnimationFrame(() => {
        const currentBatch = [...batchQueue];
        batchQueue = [];
        
        currentBatch.forEach(callback => {
          try {
            callback();
          } catch (error) {
            console.error('Batch update error:', error);
          }
        });
        
        isProcessing = false;
        
        // Process any new items that were added during processing
        if (batchQueue.length > 0) {
          processBatch();
        }
      });
    };
    
    return (callback: () => void) => {
      batchQueue.push(callback);
      processBatch();
    };
  })(),

  // Optimize scroll performance during template selection - محسن لتمرير القوالب
  optimizedScroll: (target: number, duration: number = 300) => {
    if (typeof window === 'undefined') return;
    
    const start = window.pageYOffset;
    const distance = target - start;
    const startTime = performance.now();
    
    const easeInOutCubic = (t: number) => {
      return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
    };
    
    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      window.scrollTo(0, start + distance * easeInOutCubic(progress));
      
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };
    
    requestAnimationFrame(animateScroll);
  },

  // Optimize animation performance with will-change - محسن للرسوم المتحركة
  optimizeAnimationElement: (element: HTMLElement, properties: string[] = ['transform', 'opacity']) => {
    if (typeof window === 'undefined') return;
    
    element.style.willChange = properties.join(', ');
    
    // Clean up will-change after animation
    const cleanup = () => {
      element.style.willChange = 'auto';
    };
    
    // Auto cleanup after 1 second
    setTimeout(cleanup, 1000);
    
    return cleanup;
  },

  // Measure template switching performance - قياس أداء تبديل القوالب
  measureTemplateSwitch: (templateId: string) => {
    if (typeof window === 'undefined') return () => {};
    
    const start = performance.now();
    const startMemory = performanceUtils.getMemoryUsage();
    
    return () => {
      const end = performance.now();
      const endMemory = performanceUtils.getMemoryUsage();
      
      console.log(`Template switch to ${templateId}:`, {
        duration: `${end - start}ms`,
        memoryDelta: startMemory && endMemory ? {
          used: endMemory.used - startMemory.used,
          total: endMemory.total - startMemory.total
        } : null
      });
    };
  }
};

// Image optimization utilities - أدوات تحسين الصور
export const imageUtils = {
  // Lazy load images with Intersection Observer
  lazyLoadImage: (img: HTMLImageElement) => {
    if (typeof window === 'undefined') return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          const src = img.dataset.src;
          if (src) {
            img.src = src;
            img.classList.remove('lazy-load-optimized');
            img.classList.add('loaded');
            observer.unobserve(img);
          }
        }
      });
    });
    
    observer.observe(img);
    return () => observer.unobserve(img);
  },

  // Optimize image loading for performance
  optimizeImage: (img: HTMLImageElement) => {
    if (typeof window === 'undefined') return;
    
    // Set loading attribute for lazy loading
    img.loading = 'lazy';
    
    // Add error handling
    img.onerror = () => {
      img.style.display = 'none';
      console.warn('Image failed to load:', img.src);
    };
    
    // Add loading state
    img.classList.add('loading-optimized');
    img.onload = () => {
      img.classList.remove('loading-optimized');
      img.classList.add('loaded-optimized');
    };
  },

  // Preload critical images
  preloadCriticalImages: (urls: string[]) => {
    if (typeof window === 'undefined') return;
    
    urls.forEach(url => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = url;
      link.as = 'image';
      document.head.appendChild(link);
    });
  }
};

// Cache utilities - أدوات التخزين المؤقت
export const cacheUtils = {
  // Simple in-memory cache
  memoryCache: new Map<string, { data: unknown; timestamp: number; ttl: number }>(),

  // Get cached data
  get: <T>(key: string): T | null => {
    const cached = cacheUtils.memoryCache.get(key);
    if (!cached) return null;
    
    const now = Date.now();
    if (now - cached.timestamp > cached.ttl) {
      cacheUtils.memoryCache.delete(key);
      return null;
    }
    
    return cached.data as T;
  },

  // Set cached data
  set: <T>(key: string, data: T, ttl: number = 300000) => {
    cacheUtils.memoryCache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  },

  // Clear cache
  clear: () => {
    cacheUtils.memoryCache.clear();
  },

  // Clear expired cache entries
  clearExpired: () => {
    const now = Date.now();
    for (const [key, value] of cacheUtils.memoryCache.entries()) {
      if (now - value.timestamp > value.ttl) {
        cacheUtils.memoryCache.delete(key);
      }
    }
  }
};

// Network utilities - أدوات الشبكة
export const networkUtils = {
  // Check network status
  getNetworkStatus: () => {
    if (typeof navigator !== 'undefined' && 'connection' in navigator) {
      const connection = (navigator as Navigator & { connection: { effectiveType: string } }).connection;
      return connection.effectiveType;
    }
    return 'unknown';
  },

  // Optimize requests based on network
  optimizeForNetwork: (callback: () => void) => {
    const networkType = networkUtils.getNetworkStatus();
    
    if (networkType === 'slow-2g' || networkType === '2g') {
      // Delay execution for slow networks
      setTimeout(callback, 1000);
    } else {
      // Execute immediately for fast networks
      callback();
    }
  }
};

// Bundle size optimization
export const bundleUtils = {
  // Track bundle size
  trackBundleSize: () => {
    if (typeof window !== 'undefined' && 'performance' in window) {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigation) {
        console.log('Bundle loaded in:', navigation.loadEventEnd - navigation.loadEventStart, 'ms');
      }
    }
  },

  // Preload critical resources
  preloadCriticalResources: (resources: string[]) => {
    resources.forEach(resource => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = resource;
      link.as = resource.endsWith('.css') ? 'style' : 'script';
      document.head.appendChild(link);
    });
  },

  // ============================================================================
  // TEMPLATE BUNDLE OPTIMIZATIONS
  // ============================================================================
  
  // Preload template styles and assets
  preloadTemplateBundle: () => {
    // تعطيل تحميل ملفات CSS/JS للقوالب نهائياً
  },

  // Optimize template loading with priority hints
  optimizeTemplateLoading: () => {
    // تعطيل تحميل ملفات CSS نهائياً
  }
};

// ============================================================================
// TEMPLATE SELECTION PERFORMANCE HOOKS
// ============================================================================

export const useTemplatePerformance = () => {
  const optimizeTemplateSwitch = (templateId: string, callback: () => void) => {
    const measureEnd = performanceUtils.measureTemplateSwitch(templateId);
    
    performanceUtils.optimizedTemplateSwitch(() => {
      callback();
      measureEnd();
    });
  };

  const preloadTemplates = (templateIds: string[]) => {
    templateIds.forEach(id => {
      performanceUtils.preloadTemplateAssets(id);
    });
  };

  const optimizeAnimation = (element: HTMLElement) => {
    return performanceUtils.optimizeAnimationElement(element);
  };

  return {
    optimizeTemplateSwitch,
    preloadTemplates,
    optimizeAnimation
  };
};

// Auto cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    cacheUtils.clearExpired();
  });
} 